import { Box } from "@mui/material";
import { useAtom } from "jotai";
import { useEffect, useRef, useState } from "react";
import { useFetchHostedPaymentsPageMutation } from "../../../api";
import { toastOptionsAtom } from "../../../atoms";
import { InvoicePaymentInitiatePayload } from "../../../types";
import Loading from "../../common/loading/Loading";
import { config } from "../../../config/default";

type Props = {
  payload: InvoicePaymentInitiatePayload | undefined;
  orgId: number;
};

export const HostedPaymentsPage = ({ payload, orgId }: Props) => {
  const hasMutated = useRef(false);
  const [hostedPaymentsPageUrl, setHostedPaymentsPageUrl] = useState<string>();
  const [, setToastOptions] = useAtom(toastOptionsAtom);
  const [isIframeLoading, setIsIframeLoading] = useState(true);
  const fetchHostedPaymentsPageMutation = useFetchHostedPaymentsPageMutation();
  useEffect(() => {
    if (payload && !hasMutated.current && payload.authTransaction) {
      hasMutated.current = true;
      setTimeout(() => {
        fetchHostedPaymentsPageMutation.mutate({
          orgId: orgId,
          accountId: payload?.accountId,
          invoiceNumber: "0",
          amount: 0,
          paymentMethod: payload.paymentMethod,
          saveCard: true,
          authTransaction: true,
        });
      }, 100);
    }

    if (payload && !hasMutated.current && !payload.authTransaction) {
      hasMutated.current = true;
      setTimeout(() => {
        fetchHostedPaymentsPageMutation.mutate({
          orgId: orgId,
          accountId: payload?.accountId,
          invoiceNumber: payload?.invoiceNumber,
          amount: payload?.invoiceAmount,

          addressLine1: payload?.address?.addressLine1,
          addressLine2: payload?.address?.addressLine2,
          city: payload?.address?.city,
          state: payload?.address?.state,
          zip: payload?.address?.zip,

          saveCard: payload?.saveCard,
          paymentMethod: payload.paymentMethod,
        });
      }, 100);
    }
  }, []);

  useEffect(() => {
    if (fetchHostedPaymentsPageMutation.isSuccess) {
      const hostedPaymentsPageUrl = fetchHostedPaymentsPageMutation.data;
      setHostedPaymentsPageUrl(hostedPaymentsPageUrl);
    }

    if (fetchHostedPaymentsPageMutation.isError) {
      setToastOptions({
        open: true,
        message: "Something went wrong",
        severity: "error",
      });
    }
  }, [
    fetchHostedPaymentsPageMutation.isSuccess,
    fetchHostedPaymentsPageMutation.isError,
    fetchHostedPaymentsPageMutation.isLoading,
    fetchHostedPaymentsPageMutation.data,
    fetchHostedPaymentsPageMutation.error,
  ]);
  useEffect(() => {
    if (!hostedPaymentsPageUrl) return;

    const script = document.createElement("script");
    script.src = config.worldpayHppLibraryUrl;

    script.async = true;

    script.onload = () => {
      try {
        const library = new (window as any).WPCL.Library();

        library.setup({
          url: hostedPaymentsPageUrl,
          type: "iframe",
          inject: "immediate",

          target: "worldpay-hpp-container",
          accessibility: true,
          language: "en",
          country: "us",
          debug: true,

          resultCallback: (response: any) => {
            const gateway = response?.gateway;
            if (!gateway) {
              return;
            }

            const transactionId = gateway.TransactionID;
            const transactionSetupId = gateway.TransactionSetupID;
            const expressResponseCode = gateway.ExpressResponseCode;
            const hasTransactionResult =
              !!transactionId &&
              !!transactionSetupId &&
              expressResponseCode !== undefined &&
              expressResponseCode !== null &&
              expressResponseCode !== "";

            if (!hasTransactionResult) {
              console.log("Ignoring HPP callback - no completed transaction:", {
                transactionId,
                transactionSetupId,
                expressResponseCode,
              });

              return;
            }

            const callbackUrl =
              `${config.api.baseUrl}/worldpay-integration-service` +
              `/v2/organizations/${orgId}` +
              `/accounts/${payload?.accountId}` +
              `/invoices/${payload?.invoiceNumber || "0"}` +
              `/hpp-callback` +
              `?TransactionSetupID=${encodeURIComponent(
                transactionSetupId || "",
              )}` +
              `&ValidationCode=${encodeURIComponent(
                gateway.ValidationCode || "",
              )}` +
              `&TransactionID=${encodeURIComponent(transactionId || "")}` +
              `&TranDT=${encodeURIComponent(gateway.TranDT || "")}` +
              `&ExpressResponseCode=${encodeURIComponent(
                expressResponseCode || "",
              )}` +
              `&ApprovedAmount=${encodeURIComponent(
                gateway.ApprovedAmount || payload?.invoiceAmount || 0,
              )}` +
              `&sc=${encodeURIComponent(payload?.saveCard === true)}` +
              `&at=${encodeURIComponent(payload?.authTransaction === true)}` +
              `&pm=${encodeURIComponent(payload?.paymentMethod || "")}` +
              (gateway.ACHAcctNumLastFour
                ? `&ACHAcctNumLastFour=${encodeURIComponent(
                    gateway.ACHAcctNumLastFour,
                  )}`
                : "");

            fetch(callbackUrl)
              .then(async (response) => {
                if (!response.ok) {
                  throw new Error(
                    `HPP callback failed with status ${response.status}`,
                  );
                }

                window.postMessage(
                  {
                    status: "payment-success",
                    message: "Payment completed successfully.",
                  },
                  "*",
                );
              })
              .catch((err) => {
                console.error("HPP callback failed", err);
              });
          },
        });
        setTimeout(() => {
          setIsIframeLoading(false);
        }, 500);
      } catch (err) {
        console.error("Failed to setup Worldpay HPP", err);
      }
    };

    script.onerror = () => {
      setToastOptions({
        open: true,
        message: "Failed to load Worldpay Hosted Payments.",
        severity: "error",
      });
    };

    document.body.appendChild(script);

    return () => {
      document.body.removeChild(script);
    };
  }, [hostedPaymentsPageUrl]);

  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        height: "100%",
        minWidth: 0,
        minHeight: 0,
        overflow: "hidden",
      }}
    >
      <Box
        id="worldpay-hpp-container"
        sx={{
          width: "100%",
          height: "100%",
          minWidth: 0,
          minHeight: 0,
          overflow: "auto",
        }}
      />

      {(!hostedPaymentsPageUrl ||
        fetchHostedPaymentsPageMutation.isLoading ||
        isIframeLoading) && (
        <Loading
          message=" "
          sx={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 10,
            backgroundColor: "#fff",
          }}
        />
      )}
    </Box>
  );
};
